import type {
  AreaTagPresentation,
  LayoutMarkerPresentation,
} from '@ironview/interactive-viewer';

import type { HeatMapIssueRange, HeatMapSummary } from '../utils/heatMap';
import {
  getHeatMapColor,
  getHeatMapLabelColor,
  getHeatMapSoftColor,
  getIssueSummaryText,
  issueCountMatchesRange,
} from '../utils/heatMap';

interface HeatMapPresentationInput {
  summary?: HeatMapSummary;
  enabled: boolean;
  showTooltips?: boolean;
  filter: HeatMapIssueRange;
}

export interface Ironview360MarkerVisibilityInput {
  selectedAreaId?: number;
  markerPersistenceId?: number;
  photoIds: readonly number[];
  selectedAreaMarkerIds?: readonly number[];
  selectedAreaPhotoIds?: readonly number[];
  includedInAnyArea: boolean;
}

export const isIronview360MarkerVisible = ({
  selectedAreaId,
  markerPersistenceId,
  photoIds,
  selectedAreaMarkerIds = [],
  selectedAreaPhotoIds = [],
  includedInAnyArea,
}: Ironview360MarkerVisibilityInput): boolean => {
  if (selectedAreaId === undefined) return !includedInAnyArea;
  return (
    (markerPersistenceId !== undefined &&
      selectedAreaMarkerIds.includes(markerPersistenceId)) ||
    selectedAreaPhotoIds.some((photoId) => photoIds.includes(photoId))
  );
};

const isFilteredOut = ({
  summary,
  enabled,
  filter,
}: HeatMapPresentationInput): boolean =>
  enabled &&
  (summary
    ? !issueCountMatchesRange(summary.counts.total, filter)
    : filter !== 'all');

export interface Ironview360AreaTagPresentationInput
  extends HeatMapPresentationInput {
  selected: boolean;
  hovered?: boolean;
}

export const getIronview360AreaTagPresentation = ({
  summary,
  enabled,
  showTooltips = true,
  filter,
  selected,
  hovered = false,
}: Ironview360AreaTagPresentationInput): AreaTagPresentation => {
  if (selected) return { visible: false };
  const count = summary?.counts.total ?? 0;
  const color = enabled ? getHeatMapColor(count) : '#2563eb';

  return {
    opacity: isFilteredOut({ summary, enabled, filter }) ? 0.18 : 1,
    pointerEvents: 'auto',
    baseFill: color,
    baseFillOpacity: enabled ? 0.18 : 0,
    fill: hovered ? color : enabled ? getHeatMapSoftColor(count) : '#2563eb',
    fillOpacity: hovered ? (enabled ? 0.42 : 0.28) : enabled ? 0.5 : 0.2,
    underlayStroke: color,
    underlayStrokeOpacity: 0.3,
    underlayStrokeWidth: 2.8,
    stroke: color,
    strokeOpacity: hovered ? 1 : enabled ? 0.82 : 0.92,
    strokeWidth: hovered ? 2.5 : 2.2,
    cursor: 'pointer',
    title: showTooltips && enabled && summary ? getIssueSummaryText(summary) : undefined,
  };
};

export interface Ironview360MarkerPresentationInput
  extends HeatMapPresentationInput {
  visible: boolean;
  editing: boolean;
  disabled?: boolean;
  finished: boolean;
  hasPhotos: boolean;
}

export const getIronview360MarkerPresentation = ({
  summary,
  enabled,
  showTooltips = true,
  filter,
  visible,
  editing,
  disabled = false,
  finished,
  hasPhotos,
}: Ironview360MarkerPresentationInput): LayoutMarkerPresentation => {
  const count = summary?.counts.total ?? 0;
  const color = getHeatMapColor(count);
  return {
    visible: visible || editing,
    cursor: disabled ? 'default' : editing ? 'move' : 'pointer',
    opacity: isFilteredOut({ summary, enabled, filter })
      ? 0.2
      : disabled
        ? 0.7
        : 1,
    fill: enabled ? color : finished ? '#4477ff' : 'red',
    labelFill: enabled ? getHeatMapLabelColor(count) : 'white',
    haloFill: color,
    title: !showTooltips || editing
      ? undefined
      : !enabled && !hasPhotos
        ? 'No photos available at this location yet'
        : enabled
          ? getIssueSummaryText(summary)
          : '',
  };
};
